import { ErrorModal, getMomentTz, HelmetDexteel } from "@dexteel/mesf-core";
import { Box, Grid2, Paper, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { ATHENS_GRAY } from "@/shared/colors";
import { Shift } from "@/shared/models/Shift";
import { getShifts } from "@/shared/repositories/ShiftRepository";
import {
	BreakBannerRow,
	BreakCountdownPill,
} from "../weldlines/components/BreakCountdown";
import { ShiftHeaderPill } from "../weldlines/components/ShiftHeaderPill";
import { OverviewHeader } from "../weldlines/models/types";
import { CycleTimesCard } from "./components/CycleTimesCard";
import { EntryProductCard } from "./components/EntryProductCard";
import { FinishBundleCard } from "./components/FinishBundleCard";
import { LastSawChecksCard } from "./components/LastSawChecksCard";
import { LiveCutDataCard } from "./components/LiveCutDataCard";
import { NextProductCard } from "./components/NextProductCard";
import { OutgoingBundleCard } from "./components/OutgoingBundleCard";
import { Unavailable } from "./components/primitives";
import { QualityCheckForm } from "./components/QualityCheckForm";
import type { SawQualityCheck } from "./repositories/sawWriteRepository";
import { RunningProductCard } from "./components/RunningProductCard";
import { SawShiftGantt } from "./components/SawShiftGantt";
import { ScheduleBudgetCard } from "./components/ScheduleBudgetCard";
import { ShiftCard } from "./components/ShiftCard";
import { useSawOverviewData } from "./hooks/useSawOverviewData";
import { PendingCutsCount } from "./components/PendingCutsCount";

const moment = getMomentTz();

// Cards are grouped into equal-height quarters that read as the line's story:
// the product (running → live cut → next → outgoing), shift performance
// (previous → current → scheduled/budget → cycle times), material (entry →
// finish → QC form, double-wide), then the shift's checks table full width.
// Quarters keep every row short enough for the page to sit above the fold on
// a 1080p screen.
const quarter = { xs: 12, md: 6, lg: 3 } as const;
const half = { xs: 12, md: 12, lg: 6 } as const;

// Shift chip tints: grey for the previous shift, green for the current one.
const CURRENT = { accent: "#4CAF50", dark: "#1b5e20" };
const PREVIOUS = { accent: "#90A4AE", dark: "#455160" };

// LineState → badge palette. Keeps the same NoDataBadge shape Roger already
// knows; only the dot/border/copy change with the live state. UNKNOWN keeps
// the grey "NO DATA" look so a missed read isn't visually alarming.
type BadgeTone = {
	label: string;
	dot: string;
	text: string;
	border: string;
};
const BADGE_BY_STATE: Record<string, BadgeTone> = {
	RUNNING: {
		label: "RUNNING",
		dot: "#4CAF50",
		text: "#1b5e20",
		border: "#66bb6a",
	},
	// PLC "start w/ cut ok": ready, waits for the operator's start
	CUTTING_OK: {
		label: "READY",
		dot: "#F2B600",
		text: "#7a5700",
		border: "#f2b600",
	},
	IDLE: { label: "IDLE", dot: "#F2B600", text: "#7a5700", border: "#f2b600" },
	PAUSED: {
		label: "PAUSED",
		dot: "#F2B600",
		text: "#7a5700",
		border: "#f2b600",
	},
	STOPPED: {
		label: "STOPPED",
		dot: "#e53935",
		text: "#b71c1c",
		border: "#ef5350",
	},
	CANNOT_START: {
		label: "CANNOT START",
		dot: "#ff9800",
		text: "#8a4b00",
		border: "#ffb74d",
	},
	IN_DELAY: {
		label: "IN DELAY",
		dot: "#e53935",
		text: "#b71c1c",
		border: "#ef5350",
	},
	UNKNOWN: {
		label: "NO DATA",
		dot: "#9e9e9e",
		text: "#757575",
		border: "#bdbdbd",
	},
};

const LineStateBadge = ({ state }: { state: string | null | undefined }) => {
	const tone = BADGE_BY_STATE[state ?? "UNKNOWN"] ?? BADGE_BY_STATE.UNKNOWN;
	return (
		<Box
			sx={{
				px: 1.25,
				py: 0.4,
				borderRadius: 1,
				display: "flex",
				alignItems: "center",
				gap: 0.75,
				backgroundColor: `${tone.dot}1A`,
				border: `1px solid ${tone.border}`,
			}}
		>
			<Box
				sx={{
					width: 9,
					height: 9,
					borderRadius: "50%",
					backgroundColor: tone.dot,
				}}
			/>
			<Typography
				sx={{
					fontSize: "0.7rem",
					fontWeight: 700,
					color: tone.text,
					letterSpacing: 0.4,
				}}
			>
				{tone.label}
			</Typography>
		</Box>
	);
};

// The saw's own header (RS1, 8h scheme) drives the pill, gantt window and
// shift cards; this plant-schedule fallback only fills in while the saw
// catAsset isn't wired yet.
const toHeader = (shift: Shift): OverviewHeader => ({
	AssetId: 0,
	AssetName: "20th Street Saw",
	ServerTime: new Date(),
	CurrShiftId: shift.CurrentShiftId,
	CurrShiftStart: shift.CurrentStart,
	CurrShiftEnd: shift.CurrentEnd,
	CurrShiftLabel: shift.CurrentShift,
	CurrCrew: shift.CurrentCrew,
	PrevShiftId: shift.PreviousShiftId,
	PrevShiftStart: shift.PreviousStart,
	PrevShiftEnd: shift.PreviousEnd,
	PrevShiftLabel: shift.PreviousShift,
	PrevCrew: shift.PreviousCrew,
});

const shiftSubtitle = (
	label: string | null | undefined,
	crew: string | null | undefined,
): string | undefined =>
	label || crew ? `Shift ${label ?? "—"} · Crew ${crew ?? "—"}` : undefined;

export const SawlineOverviewPage = () => {
	const today = new Date();

	const [error, setError] = useState("");
	// Check picked for correction on the shift table → loads into the QC form.
	const [editingCheck, setEditingCheck] = useState<SawQualityCheck | null>(
		null,
	);
	const handleError = useCallback((e: Error) => setError(e.message), []);

	// Live saw overview (RS1 header + RS2 signals + RS3 entry scan → slices).
	const {
		data: overview,
		isLoading: overviewLoading,
		refetch: refetchOverview,
	} = useSawOverviewData({
		onError: handleError,
	});

	// Plant shift schedule — fallback when the saw's own catAsset isn't wired
	// yet; stops polling once the overview header resolves.
	const { data: shifts, isLoading: shiftsLoading } = useQuery<Shift[], Error>({
		queryKey: ["sawShift"],
		queryFn: () => getShifts(null),
		enabled: !overview?.header?.AssetId,
		refetchInterval: 60000,
		refetchOnWindowFocus: true,
	});

	// Pull the freshly-scanned entry bundle back from RS3 right away.
	const handleScanned = useCallback(() => {
		refetchOverview();
	}, [refetchOverview]);

	// Stable handlers — the checks grid rebuilds its columns when these change.
	// Fresh object so re-clicking Edit on the same row re-loads it.
	const handleEditCheck = useCallback(
		(check: SawQualityCheck) => setEditingCheck({ ...check }),
		[],
	);
	const handleDeletedCheck = useCallback(
		(check: SawQualityCheck) =>
			setEditingCheck((prev) =>
				prev?.QualityCheckId === check.QualityCheckId ? null : prev,
			),
		[],
	);

	const shift = shifts?.[0] ?? null;
	const fallbackHeader = shift ? toHeader(shift) : null;
	// Prefer the SP's own header (real AssetName + shift assignment) when it
	// returns a non-empty AssetId; otherwise fall back to the plant pill.
	const header =
		overview?.header && overview.header.AssetId
			? overview.header
			: fallbackHeader;
	const shiftLoading = shiftsLoading && overviewLoading;

	const live = overview?.live ?? null;
	const lineState = live?.LineState ?? null;
	// No data counts as running: a missed read must not raise the restart.
	const isRunning = ["RUNNING", "UNKNOWN"].includes(lineState ?? "UNKNOWN");
	// Open delay the state does not explain: the saw reports running, or nothing.
	const badgeState =
		live?.IsInDelay === 1 &&
		(lineState == null || lineState === "RUNNING" || lineState === "UNKNOWN")
			? "IN_DELAY"
			: lineState;

	// Real saw AssetId from the SP header (the fallback pill carries AssetId 0,
	// which would fail SP validation), else null so writes stay disabled.
	const sawAssetId =
		overview?.header?.AssetId && overview.header.AssetId > 0
			? overview.header.AssetId
			: null;

	return (
		<>
			<HelmetDexteel title="20th Street Saw Overview" />

			<Box sx={{ p: 1.5, backgroundColor: ATHENS_GRAY, minHeight: "100%" }}>
				<Grid2 container spacing={1}>
					{/* Header */}
					<Grid2 size={{ xs: 12 }}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								flexWrap: "wrap",
							}}
						>
							<Typography variant="h5" sx={{ fontWeight: 600 }}>
								20th Street Saw Overview
							</Typography>
							<LineStateBadge state={badgeState} />
							{sawAssetId != null && <PendingCutsCount assetId={sawAssetId} />}
							{live?.HistorianReadAt && (
								<Typography
									variant="caption"
									sx={{ color: "#666", fontStyle: "italic" }}
									title={moment(live.HistorianReadAt).format(
										"YYYY-MM-DD HH:mm:ss",
									)}
								>
									Last updated at{" "}
									{moment(live.HistorianReadAt).format("HH:mm:ss")}
								</Typography>
							)}

							<Box sx={{ flexGrow: 1 }} />

							<BreakCountdownPill
								breaks={overview?.breaks}
								serverTime={overview?.header?.ServerTime}
								isRunning={isRunning}
							/>
							<ShiftHeaderPill
								today={today}
								header={header}
								loading={shiftLoading}
							/>
						</Box>
					</Grid2>

					<BreakBannerRow
						breaks={overview?.breaks}
						serverTime={overview?.header?.ServerTime}
						isRunning={isRunning}
					/>

					{/* Empty SAW-teal gantt lane over the active shift window */}
					<Grid2 size={{ xs: 12 }}>
						<Paper elevation={1} sx={{ p: 0.75 }}>
							<SawShiftGantt
								shiftStart={header?.CurrShiftStart ?? null}
								shiftEnd={header?.CurrShiftEnd ?? null}
								assetId={overview?.header?.AssetId}
								loading={shiftLoading}
							/>
						</Paper>
					</Grid2>

					{/* The product: what's running → the cut → what's next → what just came out */}
					<Grid2 container spacing={1} size={{ xs: 12 }} alignItems="stretch">
						<Grid2 size={quarter}>
							<RunningProductCard
								data={overview?.runningProduct}
								progress={overview?.runProgress}
								load={overview?.entryProduct}
							/>
						</Grid2>
						<Grid2 size={quarter}>
							<LiveCutDataCard data={overview?.runningProduct} />
						</Grid2>
						<Grid2 size={quarter}>
							{/* Null slice = no order queued; the card renders "—" */}
							<NextProductCard data={overview?.nextProduct} />
						</Grid2>
						<Grid2 size={quarter}>
							<OutgoingBundleCard
								data={overview?.outgoingBundle}
								assetId={sawAssetId}
								bundleId={live?.ExitingBundleId ?? null}
								onReclassified={refetchOverview}
							/>
						</Grid2>
					</Grid2>

					{/* Shift performance: previous → current → targets → cycle times */}
					<Grid2 container spacing={1} size={{ xs: 12 }} alignItems="stretch">
						<Grid2 size={quarter}>
							<Unavailable fill active={!overview?.previousShift}>
								<ShiftCard
									title="Previous Shift"
									subtitle={shiftSubtitle(
										header?.PrevShiftLabel,
										header?.PrevCrew,
									)}
									accent={PREVIOUS.accent}
									accentDark={PREVIOUS.dark}
									metrics={overview?.previousShift}
								/>
							</Unavailable>
						</Grid2>
						<Grid2 size={quarter}>
							<ShiftCard
								title="Current Shift"
								subtitle={shiftSubtitle(
									header?.CurrShiftLabel,
									header?.CurrCrew,
								)}
								accent={CURRENT.accent}
								accentDark={CURRENT.dark}
								metrics={overview?.currentShift}
								includeRun
							/>
						</Grid2>
						<Grid2 size={quarter}>
							<ScheduleBudgetCard data={overview?.scheduleBudget} />
						</Grid2>
						<Grid2 size={quarter}>
							<CycleTimesCard data={overview?.scheduleBudget} />
						</Grid2>
					</Grid2>

					{/* Material: entry scan → finish bundle → its quality check */}
					<Grid2 container spacing={1} size={{ xs: 12 }} alignItems="stretch">
						<Grid2 size={quarter}>
							{/* Always interactive — the scan button lives here, so no
							    Unavailable veil; the body shows "—" until a scan lands. */}
							<EntryProductCard
								data={overview?.entryProduct}
								assetId={sawAssetId}
								onScanned={handleScanned}
								onError={handleError}
							/>
						</Grid2>
						<Grid2 size={quarter}>
							<FinishBundleCard data={overview?.finishBundle} />
						</Grid2>
						<Grid2 size={half}>
							<QualityCheckForm
								assetId={sawAssetId}
								bundleId={live?.ExitingBundleId ?? null}
								editing={editingCheck}
								onEditDone={() => setEditingCheck(null)}
								onError={handleError}
							/>
						</Grid2>
					</Grid2>

					{/* The shift's checks, full width */}
					<Grid2 size={{ xs: 12 }}>
						<LastSawChecksCard
							assetId={sawAssetId}
							onEdit={handleEditCheck}
							onDeleted={handleDeletedCheck}
							onError={handleError}
						/>
					</Grid2>
				</Grid2>
			</Box>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
